import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import DynamicTextarea from "src/v2/components/Input/DynamicTextarea"
import TextFieldInput from "src/v2/components/Input/text-field-input"
import DatePickerSelectV2 from "src/v2/components/Date/date-picker-select"
import ButtonWithIcon from "src/v2/components/Button/Button"
import { ReactComponent as CheckIcon } from "src/v2/assets/icons/check-icon.svg"
import theme from "src/v2/theme/theme"
import dayjs from "dayjs"

const isEmpty = (value) => value === "" || value === null || value === undefined

const getValueError = (field, value) => {
    if (field.required && isEmpty(value)) return "Campo obrigatório."
    if (field.type === "number" && !isEmpty(value) && Number.isNaN(Number(value))) return "Informe um número válido."
    return null
}

export const buildHistoryPayload = (fields, values) => fields.reduce((payload, field) => ({
    ...payload,
    [field.name]: field.type === "number" && values[field.name] !== "" && values[field.name] !== null && values[field.name] !== undefined
        ? Number(values[field.name])
        : values[field.name],
}), { ...values })

const CompanyHistoryForm = ({ open, title, fields, initialRow, saving, onClose, onSave }) => {
    const [values, setValues] = useState(initialRow)
    const [errors, setErrors] = useState({})

    useEffect(() => {
        setValues(initialRow)
        setErrors({})
    }, [initialRow])

    const handleFieldChange = (field, value) => {
        setValues((current) => ({ ...current, [field.name]: value }))
        setErrors((currentErrors) => {
            if (!currentErrors[field.name]) return currentErrors
            const remainingErrors = { ...currentErrors }
            delete remainingErrors[field.name]
            return remainingErrors
        })
    }

    const handleSubmit = async (event) => {
        event.preventDefault()
        const nextErrors = fields.reduce((currentErrors, field) => {
            const error = getValueError(field, values?.[field.name])
            return error ? { ...currentErrors, [field.name]: error } : currentErrors
        }, {})
        if (Object.keys(nextErrors).length > 0) {
            setErrors(nextErrors)
            return
        }
        const saved = await onSave(buildHistoryPayload(fields, values))
        if (saved) onClose()
    }

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md" PaperProps={{ sx: { backgroundColor: theme.palette.background.paper, color: theme.palette.common.white } }}>
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                <Typography component="div" variant="h3" color="common.white">{title}</Typography>
                <IconButton aria-label={`Fechar ${title}`} onClick={onClose} disabled={saving} sx={{ color: theme.palette.icon.main, "&:focus-visible": { outline: `2px solid ${theme.palette.common.white}`, outlineOffset: 2 } }}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: "12px !important" }}>
                <Box component="form" onSubmit={handleSubmit}>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xxxs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 2 }}>
                        {fields.map((field) => field.type === "date" ? (
                            <DatePickerSelectV2
                                key={field.name}
                                label={field.label}
                                value={values?.[field.name] ?? ""}
                                size="compact"
                                onChange={(value) => handleFieldChange(
                                    field,
                                    value && dayjs(value).isValid() ? dayjs(value).format("YYYY-MM-DD") : ""
                                )}
                                defaultToToday={false}
                                required={field.required}
                                error={Boolean(errors[field.name])}
                                helperText={errors[field.name]}
                            />
                        ) : field.multiline && field.maxLength !== undefined ? (
                            <DynamicTextarea
                                key={field.name}
                                name={field.name}
                                label={field.label}
                                value={values?.[field.name] ?? ""}
                                onChange={(event) => handleFieldChange(field, event.target.value)}
                                characterLimit={field.maxLength}
                                minRows={1}
                                maxRows={6}
                                required={field.required}
                                error={Boolean(errors[field.name])}
                                helperText={errors[field.name]}
                            />
                        ) : (
                            <TextFieldInput
                                key={field.name}
                                label={field.label}
                                type={field.type ?? "text"}
                                multiline={field.multiline}
                                minRows={field.multiline ? 1 : undefined}
                                fullWidth
                                required={field.required}
                                error={Boolean(errors[field.name])}
                                helperText={errors[field.name]}
                                value={values?.[field.name] ?? ""}
                                onChange={(event) => handleFieldChange(field, event.target.value)}
                            />
                        ))}
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3 }}>
                        <ButtonWithIcon size="md" icon={<CheckIcon />} type="submit" disabled={saving}>{saving ? "SALVANDO..." : "SALVAR"}</ButtonWithIcon>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    )
}

CompanyHistoryForm.propTypes = { open: PropTypes.bool.isRequired, title: PropTypes.string.isRequired, fields: PropTypes.array.isRequired, initialRow: PropTypes.object.isRequired, saving: PropTypes.bool.isRequired, onClose: PropTypes.func.isRequired, onSave: PropTypes.func.isRequired }

export default CompanyHistoryForm
